import {
  normalizeAppDeploymentUrl,
  parseAppDeploymentReloadBudget,
  parseDeployedAppEntryUrl,
} from '../_helpers/appDeploymentRefresh.helper.js';

const RELOAD_BUDGET_KEY = 'crypto_app_deployment_reload_budget_v1';
const MAX_AUTO_RELOADS_PER_SESSION = 1;
const DEFAULT_MIN_CHECK_INTERVAL_MS = 30000;

const safeStorageRead = (storage, key) => {
  try {
    return storage?.getItem(key) || '';
  } catch {
    return '';
  }
};

const safeStorageWrite = (storage, key, value) => {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const claimReloadBudget = ({ storage, entryUrl, reason, now }) => {
  const budget = parseAppDeploymentReloadBudget(safeStorageRead(storage, RELOAD_BUDGET_KEY));
  if (budget.used >= MAX_AUTO_RELOADS_PER_SESSION) return false;
  return safeStorageWrite(storage, RELOAD_BUDGET_KEY, JSON.stringify({
    used: budget.used + 1,
    entryUrl,
    reason,
    claimedAt: Number(now()),
  }));
};

export const createAppDeploymentRefreshController = ({
  enabled = true,
  currentEntryUrl,
  windowObject,
  fetchImpl = windowObject?.fetch?.bind(windowObject),
  now = Date.now,
  minCheckIntervalMs = DEFAULT_MIN_CHECK_INTERVAL_MS,
} = {}) => {
  const location = windowObject?.location;
  const storage = (() => {
    try {
      return windowObject?.sessionStorage || null;
    } catch {
      return null;
    }
  })();
  const baseUrl = location?.href || 'http://localhost/';
  const currentEntry = normalizeAppDeploymentUrl(currentEntryUrl, baseUrl);
  const rootUrl = normalizeAppDeploymentUrl('/', baseUrl);
  let lastCheckStartedAt = 0;
  let inFlight = null;

  const reloadOnce = ({ entryUrl, reason }) => {
    if (!claimReloadBudget({ storage, entryUrl, reason, now })) return false;
    location?.reload?.();
    return true;
  };

  const check = ({ force = false } = {}) => {
    if (!enabled) return Promise.resolve({ status: 'disabled' });
    if (inFlight) return inFlight;
    if (!currentEntry || !rootUrl || typeof fetchImpl !== 'function') {
      return Promise.resolve({ status: 'unavailable' });
    }

    const startedAt = Number(now());
    if (!force && startedAt - lastCheckStartedAt < minCheckIntervalMs) {
      return Promise.resolve({ status: 'throttled' });
    }
    lastCheckStartedAt = startedAt;

    const probeUrl = new URL(rootUrl);
    probeUrl.searchParams.set('__app_entry_probe', String(startedAt));
    inFlight = Promise.resolve(fetchImpl(probeUrl.href, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })).then(async (response) => {
      if (!response?.ok || typeof response.text !== 'function') return { status: 'unavailable' };
      const deployedEntryUrl = parseDeployedAppEntryUrl({
        html: await response.text(),
        baseUrl: rootUrl,
        DOMParserClass: windowObject?.DOMParser,
      });
      if (!deployedEntryUrl) return { status: 'unavailable' };
      if (deployedEntryUrl === currentEntry) return { status: 'current', deployedEntryUrl };
      return reloadOnce({ entryUrl: deployedEntryUrl, reason: 'entry-changed' })
        ? { status: 'reloading', deployedEntryUrl }
        : { status: 'reload-suppressed', deployedEntryUrl };
    }).catch(() => ({ status: 'unavailable' })).finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  const recoverPreloadError = () => {
    if (!enabled) return { status: 'disabled' };
    if (!currentEntry) return { status: 'unavailable' };
    return reloadOnce({ entryUrl: currentEntry, reason: 'vite-preload-error' })
      ? { status: 'reloading', entryUrl: currentEntry }
      : { status: 'reload-suppressed', entryUrl: currentEntry };
  };

  return { check, recoverPreloadError };
};

let configuredController = null;

export const configureAppDeploymentRefresh = (options) => {
  configuredController = createAppDeploymentRefreshController(options);
  return configuredController;
};

export const checkAppDeploymentVersion = (options) => (
  configuredController?.check(options) || Promise.resolve({ status: 'not-configured' })
);

export const recoverAppDeploymentPreloadError = () => (
  configuredController?.recoverPreloadError() || { status: 'not-configured' }
);
